import { useDisclosure } from "@chakra-ui/hooks";
import { useQuery } from "@tanstack/react-query";
import CustomButton from "components/button";
import { MdDelete } from "react-icons/md";
import Card from "components/card";
import { DynamicOption } from "components/fields/SelectInput";
import OrdersForm from "components/forms/OrdersForm";
import CustomModal from "components/modals";
import SimpleTable from "components/tables/SimpleTable";
import { useState } from "react";
import { MdSensors } from "react-icons/md";
import { reqFunction } from "utils/API";
import { renderTimeJalali } from "utils/TimeUtiles";
import { renderToast } from "utils/globalUtils";

type OrderStatus = "Pending" | "Done" | "Failed" | "Scheduled";

type OrderObject = {
  order_type: {
    order_type_id: string; // UUID
    order_type_code: string; // "001"
    order_type_name: string;
    order_type_information?: any;
  };
  water_meter: {
    water_meter_serial: string;
    water_meter_name: string;
  };
  order_created_by?: {
    admin_id: string;
    admin_name: string;
    admin_lastname: string;
  } | null;

  order_parameters?: any | null;
  order_create_time: string;
  order_execute_time: null | string;
  order_status: OrderStatus;
  order_id: string;
  all_orders_number?: number;
};

type Device = {
  water_meter_name: string;
  water_meter_serial: string;
  order_mode: null | "R" | "P";
};

export type OrderType = {
  order_type_id: string; // UUID
  order_type_code: string; // "001"
  order_type_name: string;
  order_type_create_time: Date;
  all_orders_number: number;
};

type OrderTableData = Array<{
  orderName: string;
  orderCode: string;
  orderDeviceName: string;
  orderDeviceSerial: string;
  commander: string;
  orderSentTime: JSX.Element;
  orderReciveTime: JSX.Element;
  orderStatus: JSX.Element;
  orderActions: JSX.Element;
}>;

const OrdersView = () => {
  const AdminPermissions: string[] = JSON.parse(
    window.localStorage.getItem("permissions")
  );

  const [orderTypeSelect, setOrderTypeSelect] = useState<DynamicOption>(null);
  const [deviceSelect, setDeviceSelect] = useState<DynamicOption>(null);

  const {
    data: devicesData,
    isLoading: deviceIsLoading,
    status: deviceStatus,
  } = useQuery({
    queryFn: () =>
      reqFunction("watermeters/v2/admin/getAll", {
        page: 1,
        count: 100,
        user_id: null,
        project_id: null,
        water_meter_serial: null,
      }),
    queryKey: ["deviceList"],
  });

  const {
    data: orderType,
    isLoading: orderTypeIsLoading,
    status: orderTypeStatus,
  } = useQuery({
    queryFn: () =>
      reqFunction("OrderType/admin/getAll", {
        page: 1,
        count: 100,
      }),
    queryKey: ["orderTypeList"],
  });

  const {
    data: orderData,
    isLoading: orderIsLoading,
    status: orderStatus,
    refetch: ordersRefetch,
  } = useQuery({
    queryFn: () =>
      reqFunction("Order/admin/getAll", {
        page: 1,
        count: 100,
        order_meter: null,
        order_type_id: null,
        order_type_code: null,
      }),
    queryKey: ["orderList"],
  });

  const deleteOrderClick = async (id: string) => {
    const response = await reqFunction("Order/admin/delete", {
      order_id: id,
    });

    if (response.code === 200) {
      renderToast("دستور با موفقیت حذف شد.", "success");
      ordersRefetch();
    } else {
      renderToast(
        response?.farsi_message
          ? response.farsi_message
          : "در حذف دستور مشکلی رخ داده",
        "err"
      );
    }
  };

  const {
    isOpen: isCreateOrderOpen,
    onOpen: onCreateOrderOpen,
    onClose: onCreateOrderClose,
  } = useDisclosure();

  const projectTableHeader = [
    { title: "نام دستور", headerKey: "orderName" },
    { title: "کد دستور", headerKey: "orderCode" },
    { title: "نام دستگاه", headerKey: "orderDeviceName" },
    { title: "سریال دستگاه", headerKey: "orderDeviceSerial" },
    { title: "فرمان دهنده", headerKey: "commander" },
    { title: "زمان ارسال", headerKey: "orderSentTime" },
    { title: "زمان دریافت", headerKey: "orderReciveTime" },
    { title: "وضعیت دستور", headerKey: "orderStatus" },
  ];

  if (AdminPermissions.includes("Joker")) {
    projectTableHeader.push({ title: "عملیات", headerKey: "orderActions" });
  }

  const renderDeviceActions = (orderId: string) => {
    return (
      <div className=" flex items-center justify-center">
        {AdminPermissions.includes("Joker") ? (
          <CustomButton
            onClick={() => {
              if (window.confirm("آیا از حذف این رکورد اطمینان دارید؟")) {
                deleteOrderClick(orderId);
              }
            }}
            icon={<MdDelete />}
            color="red"
            extra="!p-2"
          />
        ) : (
          <></>
        )}
      </div>
    );
  };

  const renderStatusBadge = (status: OrderStatus) => {
    return (
      <span
        className={`rounded ${
          status === "Pending"
            ? "bg-red-600"
            : status === "Scheduled"
            ? "bg-yellow-600"
            : status === "Done"
            ? " bg-green-600"
            : "bg-red-600"
        } px-1 text-sm text-white`}
      >
        {status === "Pending"
          ? "در انتظار"
          : status === "Scheduled"
          ? "زمان‌بندی شده"
          : status === "Done"
          ? "انجام شده"
          : "خطا"}
      </span>
    );
  };

  const tableData = () => {
    let projectTableData: OrderTableData = [];
    if (
      !orderIsLoading &&
      orderStatus !== "pending" &&
      !deviceIsLoading &&
      deviceStatus !== "pending"
    ) {
      orderData.data?.forEach((obj: OrderObject) =>
        projectTableData.push({
          orderName: obj.order_type.order_type_name,
          orderCode: obj.order_type.order_type_code,
          orderDeviceName: obj.water_meter.water_meter_name,
          orderDeviceSerial: obj.water_meter.water_meter_serial,
          commander: obj.order_created_by
            ? `${obj.order_created_by.admin_name} ${obj.order_created_by.admin_lastname}`
            : "",
          orderSentTime: renderTimeJalali(obj.order_create_time, "chart"),
          orderReciveTime: renderTimeJalali(obj.order_execute_time, "chart"),
          orderStatus: renderStatusBadge(obj.order_status),
          orderActions: renderDeviceActions(obj.order_id),
        })
      );
    }
    return projectTableData;
  };

  const renderDeviceSelectData = () => {
    let selectData: DynamicOption[] = [];
    if (!deviceIsLoading && deviceStatus === "success") {
      devicesData?.data?.forEach((device: Device) =>
        selectData.push({
          label: device.water_meter_name,
          value: device.water_meter_serial,
        })
      );
    }
    return selectData;
  };

  // IMPORTANT: value must be UUID for backend create,
  // label includes code so OrdersForm can still build UI by code without changing design.
  const renderOrderTypeSelectData = () => {
    let orderTypes: { label: string; value: string }[] = [];
    if (!orderTypeIsLoading && orderTypeStatus !== "pending") {
      orderType?.data?.forEach((type: OrderType) =>
        orderTypes.push({
          label: `${type.order_type_code} - ${type.order_type_name}`,
          value: type.order_type_id, // UUID
        })
      );
    }
    return orderTypes;
  };

  return (
    <div className="">
      <CustomModal
        isOpen={isCreateOrderOpen}
        onClose={onCreateOrderClose}
        title={"ارسال دستور"}
        modalType="form"
        information={null}
        modalForm={
          <OrdersForm
            deviceSelectData={renderDeviceSelectData()}
            orderTypeSelectData={renderOrderTypeSelectData()}
            onClose={onCreateOrderClose}
            device={deviceSelect}
            setDevice={setDeviceSelect}
            orderType={orderTypeSelect}
            setOrderType={setOrderTypeSelect}
            updateTable={ordersRefetch}
          />
        }
      />
      <Card>
        <div className="relative flex items-center justify-between p-4">
          <div className="text-xl font-bold text-navy-700 dark:text-white">
            {`دستور ها (${
              orderIsLoading ? "loading..." : orderData?.data?.length ?? 0
            })`}
          </div>
          <div className=" moldal-btns flex items-center justify-end">
            <CustomButton
              text="ارسال دستور"
              onClick={onCreateOrderOpen}
              icon={<MdSensors />}
              color="green"
              extra="ml-4"
            />
          </div>
        </div>
        <SimpleTable
          header={projectTableHeader}
          data={tableData()}
          extra="h-[80vh]"
        />
      </Card>
    </div>
  );
};

export default OrdersView;
